<template>
  <div class="guest-home">
    <!-- Hero Section with 3 Main Services -->
    <section class="hero-section">
      <header class="hero-header">
        <h1 class="hero-title">Clearment Vertragsschutz</h1>
        <p class="hero-subtitle">
          KI-Vertragsanalyse, persönlicher Support und transparente Tarifbewertung – alles an einem Ort.
        </p>
      </header>

      <div class="services-grid">
        <!-- Clearment.AI Chat -->
        <article class="service-card" data-service="ai">
          <div class="service-icon">
            <v-icon size="40" color="#EA5545">mdi-brain</v-icon>
          </div>
          <h2 class="service-title">Clearment.AI Chat</h2>
          <p class="service-description">
            Stelle Fragen zu deinen Verträgen und erhalte sofortige Antworten basierend auf dem Clearment Klarvertrag.
          </p>
          <div class="service-features">
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Sofortige KI-Antworten</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Vertragsbasierte Analyse</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>24/7 verfügbar</span>
            </div>
          </div>
        </article>

        <!-- ClearmentCare -->
        <article class="service-card" data-service="care">
          <div class="service-icon">
            <v-icon size="40" color="#EA5545">mdi-shield-account</v-icon>
          </div>
          <h2 class="service-title">ClearmentCare Support</h2>
          <p class="service-description">
            Persönlicher Support bei Ablehnungen und Kürzungen durch dein Clearment-Team.
          </p>
          <div class="service-features">
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Persönliche Betreuung</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Ticket-System</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Expertenteam</span>
            </div>
          </div>
        </article>

        <!-- ClearmentCert -->
        <article class="service-card" data-service="cert">
          <div class="service-icon">
            <v-icon size="40" color="#EA5545">mdi-certificate</v-icon>
        </div>
          <h2 class="service-title">ClearmentCert Tarife</h2>
          <p class="service-description">
            Transparente Tarifbewertung mit Leistungs-Sicherheitsklassen (LSK 5 bis LSK 1).
          </p>
          <div class="service-features">
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>LSK 5 bis LSK 1</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Tarif-Vergleich</span>
            </div>
            <div class="feature-item">
              <v-icon size="16" color="#4caf50">mdi-check</v-icon>
              <span>Klarvertrag-Daten</span>
            </div>
          </div>
        </article>
        </div>

      <div class="cta-section">
        <v-btn
          color="#EA5545"
          size="x-large"
          variant="flat"
          @click="openLogin"
        >
          <v-icon left size="22">mdi-login</v-icon>
          Jetzt anmelden
        </v-btn>
        <p class="cta-hint">Kostenlos registrieren und alle Features nutzen</p>
      </div>
    </section>

    <section data-block="tarif" class="cert-section">
      <article data-card="panel" class="lsk-panel">
        <header class="cert-header">
          <div class="cert-icon">
            <v-icon size="32" color="#EA5545">mdi-certificate</v-icon>
          </div>
          <div>
            <h2 class="cert-title">ClearmentCert "Klarvertrag" für mehr Sicherheit</h2>
            <p class="cert-subtitle">
              Jede Leistungs-Sicherheitsklasse (LSK 5 bis LSK 1) zeigt, wie transparent und verlässlich ein Tarif die Leistungsversprechen dokumentiert.
            </p>
          </div>
        </header>
        <nav>
          <button
            v-for="tarifClass in tarifClasses"
            :key="tarifClass.key"
            type="button"
            :class="['tab', { active: activeTarifClass === tarifClass.key }]"
            @click="activeTarifClass = tarifClass.key"
          >
            <span class="dot" :style="{ backgroundColor: tarifClass.accent }"></span>
            {{ tarifClass.label }}
          </button>
        </nav>

        <article data-card="tarif">
          <header>
            <div class="tarif-header-row">
            <div>
                <p class="eyebrow">Sicherheitsklasse</p>
              <h3>
                <span :style="{ color: activeClassMeta.accent }">
                  {{ activeClassMeta.label }}
                </span>
              </h3>
            </div>
            <v-chip
                class="chip"
              size="small"
              :style="{ backgroundColor: activeClassMeta.accent + '15', color: activeClassMeta.accent }"
            >
              {{ activeClassMeta.label }}
            </v-chip>
          </div>
            <p class="muted">
                {{ activeClassMeta.description }}
              </p>
          </header>

          <div data-list>
            <div v-if="isLoadingTarife && !hasLoadedTarife" class="state">
              <v-progress-circular indeterminate color="#EA5545" size="26" />
              <span>Tarife werden geladen …</span>
            </div>
            <div v-else-if="tarifeError" class="state error">
              <v-icon color="#d14343" class="mr-2">mdi-alert-circle</v-icon>
              <span>{{ tarifeError }}</span>
            </div>
            <div v-else-if="!activeTarifeList.length" class="state">
              <v-icon color="#64748b" class="mr-2">mdi-information</v-icon>
              <span>Aktuell keine veröffentlichten Tarife in dieser Klasse.</span>
            </div>
            <template v-else>
              <article
              v-for="tarif in activeTarifeList"
              :key="`${activeClassMeta.label}-${tarif.tarif_id}`"
            >
              <div>
                  <p class="meta">{{ tarif.un_name }}</p>
                  <p class="title">{{ tarif.tarif_title }}</p>
              </div>
              <span
                  class="pill"
                :style="{ color: activeClassMeta.accent, borderColor: activeClassMeta.accent }"
              >
                {{ activeClassMeta.label }}
              </span>
              </article>
            </template>
          </div>
        </article>
      </article>
    </section>

    <section
      v-if="showTeamSection"
      data-block="team"
      class="care-team-section"
    >
      <article class="care-team-card">
        <header class="care-team-header">
          <div class="care-team-icon">
            <v-icon size="36" color="#EA5545">mdi-shield-account</v-icon>
          </div>
          <div>
            <h2 class="care-team-title">ClearmentCare – echte Menschen helfen</h2>
            <p class="care-team-subtitle">
          Unser Admin-Team betreut jeden Tarif persönlich – von der Datenpflege bis zur Eskalation bei Versicherern.
          Lerne drei Ansprechpartner:innen kennen, die deine Verträge auf dem Schirm haben.
        </p>
      </div>
        </header>
        
        <div v-if="isLoadingAdmins" class="state">
        <v-progress-circular indeterminate color="#EA5545" size="28" />
        <span>Team wird geladen …</span>
      </div>
        <div v-else-if="teamError" class="state error">
        <v-icon color="#d14343" class="mr-1">mdi-alert-circle</v-icon>
        <span>{{ teamError }}</span>
      </div>
        <div v-else class="grid" data-variant="team">
        <article
          v-for="(admin, index) in formattedAdmins"
          :key="admin.user_id || index"
            data-surface
        >
          <div
              class="avatar"
            :style="{ backgroundColor: avatarColors[index % avatarColors.length] }"
          >
            <img
              v-if="admin.thumbUrl"
              :src="admin.thumbUrl"
              :alt="admin.displayName"
            />
            <span v-else>{{ admin.initials }}</span>
          </div>
            <div>
              <p class="meta">{{ admin.company }}</p>
            <h3>{{ admin.displayName }}</h3>
              <p class="muted">{{ admin.role || 'Vertragsschutz' }}</p>
          </div>
            <div class="meta-row">
              <v-icon size="18" color="#EA5545">mdi-shield-check</v-icon>
            <span>Admin · Klarvertrag</span>
          </div>
        </article>
      </div>
      </article>
    </section>

  
    <section data-block="evidence">
      <div class="grid" data-variant="stats">
        <article v-for="stat in stats" :key="stat.label">
          <p class="stat-value">{{ stat.value }}</p>
          <p class="muted">{{ stat.label }}</p>
        </article>
      </div>
      <article data-card="quote">
        <v-icon size="40" color="#EA5545" class="mb-4">mdi-format-quote-close</v-icon>
        <p class="quote-text">
          „Clearment KI-Vertragssscan beantwortet Rückfragen unserer Mitarbeitenden in Sekunden – und wenn eine Rechnung hakt,
          übernimmt der Vertragsschutz alle Gespräche mit der Versicherung.“
        </p>
        <p class="meta">People & Culture Lead, Klinikverbund</p>
      </article>
    </section>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'

const updateDocumentHead = inject('updateDocumentHead')
const baseCalls = inject('baseCalls')
const userStore = inject('userStore')

// const valueProps = ref([
//   {
//     icon: 'mdi-shield-check',
//     color: '#EA5545',
//     title: 'Klarvertrag & Dokumente',
//     copy: 'Alle Vertrags- und Leistungsdaten werden strukturiert abgelegt – inklusive Tarifnummer, Ansprechpartner und Historie.'
//   },
//   {
//     icon: 'mdi-brain',
//     color: '#6366F1',
//     title: 'Clearment KI-Vertragssscan',
//     copy: 'Unsere KI kennt tausende Antworten zu Zahnleistungen, erfasst Rückfragen und sorgt für konsistente Kommunikation.'
//   },
//   {
//     icon: 'mdi-lifebuoy',
//     color: '#0EA5E9',
//     title: 'Vertragsschutz',
//     copy: 'Tickets landen direkt bei den Clearment Spezialist:innen. Wir prüfen Unterlagen, fordern Nachweise an und halten alle Status bereit.'
//   },
//   {
//     icon: 'mdi-file-document-edit',
//     color: '#F97316',
//     title: 'Rechnungsservice',
//     copy: 'HKP, Zahnrechnungen und Laborzettel werden digital eingereicht. Textbausteine & Versandhinweise inklusive.'
//   }
// ])

// const workflowSteps = ref([
//   {
//     title: '1. wählen Sie Ihren Vertrag',
//     copy: 'Unternehmen verknüpfen ihre Klarvertrag-IDs. Mitarbeitende wählen ihren Vertrag direkt im Portal aus.',
//     points: ['Rollen- und Rechteverwaltung', 'Alle Vertragsnummern griffbereit']
//   },
//   {
//     title: 'Fragen mit KI-Vertragssscan klären',
//     copy: 'Clearment KI-Vertragssscan greift auf dieselben Daten wie der Vertragsschutz zu – so bleiben Antworten nachvollziehbar.'
//   },
//   {
//     title: 'Ticket oder Rechnung übergeben',
//     copy: 'Offene Punkte werden an den Vertragsschutz eskaliert. Wir übernehmen Dialog, Nachforderungen und Erfolgskontrolle.',
//     points: ['Live-Status & Benachrichtigungen', 'Exports für Audits & Reporting']
//   }
// ])

// const stats = ref([
//   { value: '8.400+', label: 'beantwortete KI-Vertragssscan Fragen' },
//   { value: '3.200', label: 'aktive Klarvertrag-Datensätze' },
//   { value: '48h', label: 'Ø Bearbeitungszeit pro Ticket' }
// ])

const avatarColors = ['#e3f2fd', '#fff4e6', '#fce7f3']
const featuredAdmins = ref([])
const isLoadingAdmins = ref(false)
const teamError = ref('')

const tarifClasses = [
  {
    key: 'lsk5',
    label: 'LSK 5',
    accent: '#16a34a',
    description: 'Neueste und strengste Sicherheitsklasse mit 157 Leistungsfragen und 688 Antwortoptionen.'
  },
  {
    key: 'lsk3',
    label: 'LSK 3',
    accent: '#f97316',
    description: 'Erweiterter Fragenkatalog seit 2017 – solide Leistungstransparenz für etablierte Tarife.'
  },
  {
    key: 'lsk1',
    label: 'LSK 1',
    accent: '#64748b',
    description: 'Erste Clearment Sicherheitsklasse aus 2008 mit Fokus auf die wichtigsten Leistungsversprechen.'
  }
]

const activeTarifClass = ref(tarifClasses[0].key)
const tarifeByClass = reactive({
  lsk5: [],
  lsk3: [],
  lsk1: []
})
const isLoadingTarife = ref(false)
const hasLoadedTarife = ref(false)
const tarifeError = ref('')

const activeClassMeta = computed(() => {
  return tarifClasses.find((item) => item.key === activeTarifClass.value) ?? tarifClasses[0]
})

const activeTarifeList = computed(() => {
  return tarifeByClass[activeTarifClass.value] ?? []
})

const formattedAdmins = computed(() => {
  return featuredAdmins.value.map((admin) => {
    const displayName = admin.name || admin.full_name || admin.email || 'Vertragsschutz'
    const initials = getInitials(displayName)
    return {
      ...admin,
      displayName,
      initials,
      thumbUrl: buildUserThumb(admin.thumb),
      company: admin.company || 'Clearment'
    }
  })
})

const showTeamSection = computed(() => {
  return isLoadingAdmins.value || !!formattedAdmins.value.length || !!teamError.value
})

async function fetchTarifeByLsk(lsk) {
  if (!baseCalls?.clearmentPost) {
    return []
  }
  const response = await baseCalls.clearmentPost({
    action: 'getlsktarife',
    lsk,
    limit: 6
  })
  if (response.data?.success) {
    return response.data?.tarife ?? []
  }
  throw new Error(response.data?.message || 'Tarife konnten nicht geladen werden.')
}

async function loadTarife() {
  if (hasLoadedTarife.value || isLoadingTarife.value) {
    return
  }
  if (!baseCalls?.clearmentPost) {
    hasLoadedTarife.value = true
    return
  }
  isLoadingTarife.value = true
  tarifeError.value = ''
  try {
    const [list5, list3, list1] = await Promise.all([
      fetchTarifeByLsk(5),
      fetchTarifeByLsk(3),
      fetchTarifeByLsk(1)
    ])
    tarifeByClass.lsk5 = list5.slice(0, 5)
    tarifeByClass.lsk3 = list3.slice(0, 5)
    tarifeByClass.lsk1 = list1.slice(0, 5)
  } catch (error) {
    console.error('loadTarife', error)
    tarifeError.value = 'Tarife konnten nicht geladen werden.'
  } finally {
    isLoadingTarife.value = false
    hasLoadedTarife.value = true
  }
}

async function loadFeaturedAdmins() {
  if (!baseCalls?.clearmentPost) {
    return
  }
  isLoadingAdmins.value = true
  teamError.value = ''
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getpubliccompanyadmins',
      company_id: 1,
      limit: 3
    })
    if (response.data?.success) {
      featuredAdmins.value = response.data.admins || []
    } else {
      teamError.value = response.data?.message || 'Admins konnten nicht geladen werden.'
    }
  } catch (error) {
    console.error('loadFeaturedAdmins', error)
    teamError.value = 'Admins konnten nicht geladen werden.'
  } finally {
    isLoadingAdmins.value = false
  }
}

function openLogin() {
  if (userStore && typeof userStore.setLoginView === 'function') {
    userStore.setLoginView(true)
  }
}

function buildUserThumb(hash) {
  if (!hash) {
    return null
  }
  return `/getfile/${hash}`
}

function getInitials(name) {
  if (!name) {
    return 'CM'
  }
  const parts = name.trim().split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}

onMounted(() => {
  updateDocumentHead?.(
    'Clearment – Digitale Klarheit für Zahnzusatz-Verträge',
    'Transparente Leistungen, KI-Antworten und strukturierter Support für dein Team.'
  )
  loadTarife()
  loadFeaturedAdmins()
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/modules/HomeGuestContent.css"></style>
